import React, { useState, useEffect, useRef } from "react";
import {
  useGetProgramChecklistsQuery,
  useLinkChecklistToProgramMutation,
  useUnlinkChecklistFromProgramMutation,
  useUnlinkChecklistFromRunMutation,
  useGetChecklistTemplatesQuery,
  useFreezeProgramChecklistMutation,
  useUnfreezeProgramChecklistMutation,
  useGetRunChecklistsQuery,
  useLinkTemplateToRunMutation,
  ProgramChecklist,
  useFinalizeAdminReviewMutation
} from "../../utils/api/endpoints/checklists/checklistApi";
import { useGetResearcherProgramsQuery } from "../../utils/api/endpoints/checklists/researcherChecklistApi";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useProgramRuns from "../../utils/hooks/programs/useProgramRuns";
import { FiLayers, FiPlay, FiChevronDown, FiChevronRight, FiCheckCircle, FiPlus, FiLock, FiUnlock, FiTrash2, FiExternalLink, FiSearch, FiZap, FiX, FiDownload, FiAlertCircle } from "react-icons/fi";
import { generateChecklistPDF } from "./pdf/ChecklistPDFDocument";
import { ChecklistGenerator } from "./ChecklistGenerator";
import useProgram from "../../utils/hooks/programs/useProgram";
import { ResearcherAssignmentCard } from "./ResearcherAssignmentCard";
import { ConfirmationModal } from "./ConfirmationModal";

interface ProgramChecklistsViewProps {
  programId: number;
  runId?: number;
  userRole: number; // UserRole enum value
  asset?: any;
}

const RunChecklistsSection = ({ programId, run, userRole, isAdmin, canEdit, isContinuousV2, onOpenLinkModal, onOpenWizard }: { 
  programId: number; 
  run: any; 
  userRole: number;
  isAdmin: boolean;
  canEdit: boolean;
  isContinuousV2?: boolean;
  onOpenLinkModal: (runId: number) => void;
  onOpenWizard: (runId: number) => void;
}): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: runChecklistsData, isLoading, refetch } = useGetRunChecklistsQuery(run.run_id);
  const [freezeChecklist] = useFreezeProgramChecklistMutation();
  const [unfreezeChecklist] = useUnfreezeProgramChecklistMutation();
  const [unlinkChecklistFromRun] = useUnlinkChecklistFromRunMutation();
  const navigate = useNavigate();

  const [selectedRunChecklist, setSelectedRunChecklist] = useState<ProgramChecklist | null>(null);
  const [finalizeAdminReview] = useFinalizeAdminReviewMutation();
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: (val?: string) => void;
    type: "info" | "danger" | "warning" | "success";
    confirmLabel?: string;
    showInput?: boolean;
    inputPlaceholder?: string;
    inputValueRequired?: boolean;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "info",
  });

  const handleFreeze = async (checklistId: number, name: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Freeze Checklist',
      message: `Freeze "${name}"? It will be visible to business users.`,
      type: "warning",
      confirmLabel: "Freeze",
      onConfirm: async () => {
        const freezePromise = freezeChecklist(checklistId).unwrap();
        toast.promise(freezePromise, {
          loading: 'Freezing...',
          success: 'Checklist Frozen',
          error: (err) => err?.data?.error || err?.data?.message || err?.message || 'Failed to freeze'
        });
        try {
          await freezePromise;
          if (selectedRunChecklist?.run_checklist_id === checklistId) {
            setSelectedRunChecklist(prev => prev ? { ...prev, is_frozen: true } : null);
          }
          refetch();
        } catch (e) {}
      }
    });
  };

  const handleUnfreeze = async (checklist: ProgramChecklist) => {
    const hasAction = Number(checklist.completion_percentage) > 0;
    if (hasAction) {
      toast.error("Cannot unfreeze: researcher has already taken action on this run checklist.");
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Unfreeze Checklist',
      message: `Unfreeze "${checklist.checklist_name}"?`,
      type: "warning",
      confirmLabel: "Unfreeze",
      onConfirm: async () => {
        const unfreezePromise = unfreezeChecklist(checklist.run_checklist_id!).unwrap();
        toast.promise(unfreezePromise, {
          loading: 'Unfreezing...',
          success: 'Checklist Unfrozen',
          error: (err) => err?.data?.error || err?.data?.message || err?.message || 'Failed to unfreeze'
        });
        try {
          await unfreezePromise;
          if (selectedRunChecklist?.run_checklist_id === checklist.run_checklist_id) {
            setSelectedRunChecklist(prev => prev ? { ...prev, is_frozen: false } : null);
          }
          refetch();
        } catch (e) {}
      }
    });
  };

  const handleUnlink = async (checklistId: number, name: string, completionPerc: number = 0) => {
    if (completionPerc > 0) {
      toast.error(`Cannot unlink "${name}": Researcher has already started work (${Math.round(completionPerc)}% complete).`);
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Remove Checklist',
      message: `Delete "${name}" from this run snapshot?`,
      type: "danger",
      confirmLabel: "Remove",
      onConfirm: async () => {
        const unlinkPromise = unlinkChecklistFromRun({ runId: run.run_id, checklistId }).unwrap();
        toast.promise(unlinkPromise, {
          loading: 'Removing...',
          success: 'Removed from run',
          error: (err) => err?.data?.error || err?.data?.message || err?.message || 'Failed to remove'
        });
        try {
          await unlinkPromise;
          if (selectedRunChecklist?.program_checklist_id === checklistId) {
            setSelectedRunChecklist(null);
          }
          refetch();
        } catch (e) {}
      }
    });
  };

  const checklists = runChecklistsData?.data || [];
  const isBusiness = [2, 7, 8].includes(userRole);
  const isResearcher = userRole === 1;
  const isGuest = isBusiness || isResearcher;
  const visibleChecklists = isGuest ? checklists.filter(c => c.is_frozen) : checklists;

  const totalItems = visibleChecklists.reduce((acc, c) => acc + (c.items?.length || 0), 0);
  const averageCompletion = visibleChecklists.length > 0 
    ? visibleChecklists.reduce((acc, c) => acc + Number(c.completion_percentage), 0) / visibleChecklists.length 
    : 0;

  return (
    <div className="mb-4">
      <div 
        onClick={() => {
          if (isOpen) {
            setSelectedRunChecklist(null);
          }
          setIsOpen(!isOpen);
        }}
        className={`w-full px-8 py-6 flex items-center justify-between cursor-pointer transition-all duration-300 border border-slate-800 bg-[#010D2C] text-white group ${isOpen ? 'rounded-t-xl rounded-b-none' : 'rounded-xl'}`}
      >
        <div className="flex items-center gap-5 min-w-0 flex-1">
          <div className={`w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center transition-all duration-300 ${run.status === 'active' ? 'bg-[#58CC02]/[0.17] text-[#58CC02]' : 'bg-slate-800 text-slate-400'}`}>
            <FiPlay className={`w-5 h-5 ${run.status === 'active' && 'fill-current'}`} />
          </div>
          <div className="text-left min-w-0 flex-1">
            <div className="flex items-start gap-2 mb-0.5">
              <h4 className="text-lg font-semibold tracking-tight text-white group-hover:text-[#58CC02] transition-colors line-clamp-2 leading-tight">
                Run #{run.run_id}: {run.run_title}
              </h4>
              {run.status === 'active' && (
                <span className="mt-1 px-2 py-0.5 bg-green-500/10 text-[#58CC02] text-[8px] font-black uppercase tracking-wider rounded border border-green-500/20 whitespace-nowrap flex-shrink-0">
                  Active Now
                </span>
              )}
            </div>
            <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">
              Test Run Started : {new Date(run.start_date).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-8">
          {visibleChecklists.length > 0 && (
            <div className="hidden lg:flex items-center gap-6 pr-8">
               <div className="flex flex-col items-end gap-1">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Total Progress</span>
                  <span className="text-xl font-black tabular-nums text-white leading-tight">{Math.round(averageCompletion)}%</span>
                  <div className="w-24 h-1 mt-1 rounded-full overflow-hidden bg-slate-800">
                     <div className="h-full bg-[#58CC02] shadow-[0_0_8px_rgba(88,204,2,0.4)] transition-all duration-1000" style={{ width: `${averageCompletion}%` }}></div>
                  </div>
               </div>
            </div>
          )}

            {isAdmin && (
              <div className="flex gap-3 border-l border-slate-800 pl-8 pr-8">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenWizard(run.run_id);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#EAFFE0] text-[#58CC02] rounded-lg text-[10px] font-black uppercase tracking-widest hover:brightness-95 transition-all"
                >
                  <FiZap className="w-3.5 h-3.5" /> GENERATE
                </button>
                {!isContinuousV2 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenLinkModal(run.run_id);
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all border border-slate-700 bg-[#051129] text-[#58CC02] hover:bg-[#0a1b3d]"
                  >
                     <FiPlus className="w-3 h-3" /> LINK MASTER
                  </button>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-6 border-l border-slate-800 pl-8">
               <div className="flex flex-col items-center">
                  <span className="text-lg font-black text-white">
                    {visibleChecklists.length}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Checklists
                  </span>
               </div>
               <div className="transition-transform duration-300" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <FiChevronDown className="text-white w-5 h-5" />
               </div>
            </div>
        </div>
      </div>

      {isOpen && (
         <div className="flex gap-6 items-start mt-0">
            <div className="flex-1 bg-white rounded-b-xl border-x border-b border-slate-200 overflow-hidden animate-in slide-in-from-top-4 duration-300">
              {isLoading ? (
                <div className="py-20 text-center text-xs font-bold text-slate-400 uppercase tracking-widest animate-pulse">Loading Checklists...</div>
              ) : visibleChecklists.length === 0 ? (
                <div className="py-20 text-center">
                   <FiSearch className="mx-auto w-10 h-10 text-slate-100 mb-4" />
                   <p className="text-sm font-bold text-slate-300">No checklists linked yet</p>
                </div>
              ) : (
                <table className="w-full text-left">
                  <thead className="bg-[#f8fafc] border-b border-gray-100">
                    <tr>
                      <th className="px-8 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Name</th>
                      <th className="px-8 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">Status</th>
                      <th className="px-8 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">Requirements</th>
                      <th className="px-8 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Testing Progress</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visibleChecklists.map(checklist => (
                      <tr 
                        key={checklist.program_checklist_id}
                        onClick={() => setSelectedRunChecklist(checklist)}
                        className={`group cursor-pointer transition-colors ${selectedRunChecklist?.run_checklist_id === checklist.run_checklist_id ? 'bg-blue-50/50' : 'hover:bg-slate-50/50'}`}
                      >
                        <td className="px-8 py-5">
                           <span className={`text-[13px] font-bold tracking-tight transition-colors ${selectedRunChecklist?.run_checklist_id === checklist.run_checklist_id ? 'text-blue-600' : 'text-slate-900'} group-hover:text-blue-600`}>
                             {checklist.checklist_name}
                           </span>
                        </td>
                        <td className="px-8 py-5 text-center">
                           <div className="flex items-center justify-center gap-2">
                              <div className={`w-1.5 h-1.5 rounded-full ${checklist.is_frozen ? 'bg-blue-500' : 'bg-amber-500'}`}></div>
                              <span className={`text-[10px] font-bold uppercase tracking-widest ${checklist.is_frozen ? 'text-blue-500' : 'text-amber-500'}`}>
                                {checklist.is_frozen ? 'Frozen' : 'Draft'}
                              </span>
                           </div>
                        </td>
                        <td className="px-8 py-5 text-center">
                           <span className="text-[13px] font-bold text-slate-600 tabular-nums">{checklist.items?.length || 0}</span>
                        </td>
                        <td className="px-8 py-5">
                           <div className="flex items-center gap-4">
                              <div className="flex-1 bg-slate-100 h-1 rounded-full overflow-hidden">
                                 <div className="h-full bg-[#58CC02] transition-all duration-1000" style={{ width: `${checklist.completion_percentage}%` }}></div>
                              </div>
                              <span className="text-[12px] font-bold text-slate-900 tabular-nums min-w-[35px] text-right">
                                {Math.round(Number(checklist.completion_percentage))}%
                              </span>
                           </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {selectedRunChecklist && (
              <div className="w-[400px] bg-white rounded-xl border border-slate-200 p-6 py-5 animate-in slide-in-from-right duration-300 h-fit sticky top-4">
                 <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black tracking-tighter border shadow-sm ${selectedRunChecklist.is_frozen ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-amber-50 text-amber-600 border-amber-100'}`}>
                        {selectedRunChecklist.is_frozen ? 'FROZEN' : 'DRAFT'}
                      </span>
                      {selectedRunChecklist.is_admin_reviewed && (
                        <span className="px-3 py-1 rounded-full text-[10px] font-black tracking-tighter border shadow-sm bg-green-50 text-green-600 border-green-100 flex items-center gap-1">
                          <FiCheckCircle className="w-3.5 h-3.5" />
                          APPROVED
                        </span>
                      )}
                    </div>
                    <button 
                      onClick={() => setSelectedRunChecklist(null)}
                      className="p-1 hover:bg-slate-50 rounded-lg transition-colors"
                    >
                      <FiX className="text-slate-300 w-5 h-5" />
                    </button>
                 </div>

                 <h3 className="text-lg font-bold text-slate-900 tracking-tight leading-tight mb-5 px-0">
                    {selectedRunChecklist.checklist_name}
                 </h3>

                 <div className="border-b border-slate-100 -mx-6 mb-6"></div>

                 <div className="space-y-6">
                    <div>
                       <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2">Description</span>
                       <p className="text-[12px] text-slate-500 leading-relaxed font-medium">
                          {selectedRunChecklist.description || "Official Application Security Verification Standard (ASVS) v5.0.0 (May 2025). Complete library for holistic architectural verification."}
                       </p>
                    </div>

                    <div className="bg-[#f8fafc] rounded-lg p-4 py-3.5 border border-slate-100/50">
                       <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Requirements</span>
                          <span className="text-lg font-black text-slate-900 tabular-nums">
                             {selectedRunChecklist.items?.length || 0}
                          </span>
                       </div>
                    </div>

                    <div>
                       <div className="flex items-center justify-between mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                          <span>Testing Progress</span>
                          <span className="text-slate-900 text-[11px] font-black">{Math.round(Number(selectedRunChecklist.completion_percentage))}%</span>
                       </div>
                       <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden mb-8">
                          <div className="h-full bg-[#58CC02] shadow-[0_0_8px_rgba(88,204,2,0.3)] transition-all duration-1000" style={{ width: `${selectedRunChecklist.completion_percentage}%` }}></div>
                       </div>

                       <div className="space-y-3.5">
                          <button
                            onClick={() => {
                              const path = isResearcher 
                                ? `/dashboard/researcher-checklists/programs/${programId}?runId=${run.run_id}`
                                : `/dashboard/programs/${programId}/runs/${run.run_id}/checklists/${selectedRunChecklist.run_checklist_id}`;
                              navigate(path);
                            }}
                            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-[#010D2C] text-white text-[11px] font-black uppercase tracking-widest rounded-xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-200"
                          >
                            <FiExternalLink className="w-4 h-4" />
                            Open Checklist
                          </button>

                          <DownloadChecklistPDFButton
                             checklist={selectedRunChecklist}
                             programName={run.run_title ? `Run ${run.run_id}: ${run.run_title}` : `Run ${run.run_id}`}
                             isAdmin={isAdmin}
                           />

                           {isAdmin && !selectedRunChecklist.is_admin_reviewed && !selectedRunChecklist.checklist_name?.toLowerCase().includes("download") && (
                             <button
                               onClick={() => {
                                 setConfirmModal({
                                   isOpen: true,
                                   title: "Approve & Finalize Checklist",
                                   message: "Are you sure you want to approve and finalize this checklist? Once finalized, business clients will be able to download the checklist PDF.",
                                   type: "success",
                                   confirmLabel: "Approve & Finalize",
                                   onConfirm: async () => {
                                     try {
                                       await finalizeAdminReview({ 
                                          programId, 
                                          checklistId: selectedRunChecklist.run_checklist_id || selectedRunChecklist.program_checklist_id,
                                          runId: selectedRunChecklist.run_id 
                                        }).unwrap();
                                       toast.success("Checklist approved and finalized!");
                                       setSelectedRunChecklist(prev => prev ? { ...prev, is_admin_reviewed: true } : null);
                                       refetch();
                                     } catch (err: any) {
                                       toast.error(err?.data?.message || "Failed to finalize review");
                                     }
                                   }
                                 });
                               }}
                               className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded-lg font-medium text-xs hover:bg-green-700 transition-all"
                             >
                               <FiCheckCircle className="w-4 h-4" />
                               Approve & Finalize Checklist
                             </button>
                           )}

                          {canEdit && (
                             <div className="grid grid-cols-2 gap-3.5">
                                <button
                                  onClick={() => selectedRunChecklist.is_frozen ? handleUnfreeze(selectedRunChecklist) : handleFreeze(selectedRunChecklist.run_checklist_id!, selectedRunChecklist.checklist_name)}
                                  disabled={selectedRunChecklist.is_frozen && Number(selectedRunChecklist.completion_percentage) > 0}
                                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-[10px] font-black transition-all border shadow-sm uppercase tracking-widest ${selectedRunChecklist.is_frozen ? 'bg-white border-blue-100 text-blue-600 hover:bg-blue-50 disabled:opacity-50' : 'bg-white border-blue-100 text-blue-600 hover:bg-blue-50'}`}
                                >
                                   <FiLock className="w-3.5 h-3.5" />
                                   {selectedRunChecklist.is_frozen ? 'UNFREEZE' : 'FREEZE'}
                                </button>
                                {isAdmin && (
                                   <button
                                     onClick={() => handleUnlink(selectedRunChecklist.run_checklist_id!, selectedRunChecklist.checklist_name, Number(selectedRunChecklist.completion_percentage))}
                                     disabled={Number(selectedRunChecklist.completion_percentage) > 0}
                                     className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-[10px] font-black bg-white border border-red-100 text-red-500 hover:bg-red-50 transition-all shadow-sm uppercase tracking-widest disabled:opacity-50"
                                   >
                                      <FiTrash2 className="w-3.5 h-3.5" />
                                      UNLINK
                                   </button>
                                )}
                             </div>
                          )}
                       </div>
                    </div>
                 </div>
              </div>
            )}
         </div>
      )}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        type={confirmModal.type}
        showInput={confirmModal.showInput}
        inputPlaceholder={confirmModal.inputPlaceholder}
        inputValueRequired={confirmModal.inputValueRequired}
      />
    </div>
  );
};

// ─── Download Checklist PDF Button ─────────────────────────────────────────

interface DownloadChecklistPDFButtonProps {
  checklist: ProgramChecklist;
  programName: string;
  isAdmin?: boolean;
}

const DownloadChecklistPDFButton: React.FC<DownloadChecklistPDFButtonProps> = ({
  checklist,
  programName,
  isAdmin = false,
}) => {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const isApproved = !!checklist.is_admin_reviewed || isAdmin;

  const executeDownload = async () => {
    setIsGenerating(true);
    const toastId = toast.loading("Generating Checklist PDF…");
    try {
      // Fetch full checklist items if not already loaded (they are included in the list query)
      const blob = await generateChecklistPDF({
        checklist,
        programName,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = programName.replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 60);
      link.href = url;
      link.download = `Pentest_Checklist_${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Checklist PDF downloaded", { id: toastId });
    } catch (err: any) {
      console.error("Checklist PDF generation failed:", err);
      toast.error(err?.message || "Failed to generate PDF", { id: toastId });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!isApproved || isGenerating) return;
    
    const percentage = Math.round(Number(checklist.completion_percentage || 0));
    if (!isAdmin && percentage < 100) {
      setShowConfirmModal(true);
    } else {
      await executeDownload();
    }
  };

  return (
    <div className="relative group">
      <button
        onClick={handleDownload}
        disabled={!isApproved || isGenerating}
        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-all ${
          isApproved
            ? "bg-blue-600 text-white hover:bg-blue-700 hover:translate-y-[-1px] shadow-sm"
            : "bg-slate-100 text-slate-400 cursor-not-allowed"
        }`}
      >
        {isGenerating ? (
          <>
            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            Generating PDF…
          </>
        ) : (
          <>
            <FiDownload className="w-4 h-4" />
            Download Checklist PDF
          </>
        )}
      </button>
      {!isApproved && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex whitespace-nowrap bg-slate-800 text-white text-[10px] font-medium px-2.5 py-1.5 rounded-lg shadow-lg z-10 pointer-events-none">
          Admin approval required to export
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
        </div>
      )}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          setShowConfirmModal(false);
          await executeDownload();
        }}
        title="Testing Incomplete"
        message={`This checklist is only ${Math.round(Number(checklist.completion_percentage || 0))}% completed. Are you sure you want to download the PDF report before all test cases have been finalized?`}
        confirmLabel="Download Anyway"
        cancelLabel="Cancel"
        type="warning"
      />
    </div>
  );
};

const ProgramChecklistsView: React.FC<ProgramChecklistsViewProps> = ({

  programId,
  runId: initialRunId,
  userRole,
  asset
}) => {
  const navigate = useNavigate();
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [viewRunId, setViewRunId] = useState<number | undefined>(initialRunId);
  const [modalTargetRunId, setModalTargetRunId] = useState<number | undefined>(undefined);
  const [wizardTargetRunId, setWizardTargetRunId] = useState<number | undefined>(undefined);
  const [linkTemplateToRun] = useLinkTemplateToRunMutation();
  const [activeTab, setActiveTab] = useState<'master' | 'history'>('master');
  const [selectedChecklist, setSelectedChecklist] = useState<ProgramChecklist | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: (val?: string) => void;
    type: "info" | "danger" | "warning" | "success";
    confirmLabel?: string;
    showInput?: boolean;
    inputPlaceholder?: string;
    inputValueRequired?: boolean;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "info",
  });

  const isBusiness = [2, 7, 8].includes(userRole);
  const isResearcher = userRole === 1;
  const isProgramGuest = isBusiness || isResearcher;

  // Sync viewRunId when initialRunId changes (e.g. from URL navigation)
  React.useEffect(() => {
    setViewRunId(initialRunId);
  }, [initialRunId]);

  const runId = viewRunId;
  const activeRunId = modalTargetRunId || runId;

  // Administrative permissions (Admin, Admin Manager, QA, Sub-Admin)
  const isAdmin = [3, 4, 5, 9].includes(userRole);
  const canEdit = isAdmin;

  const { program, refetch: refetchProgramData } = useProgram(programId);
  const isV2 = program?.architecture_version === 2;
  const isContinuousV2 = isV2 && program?.scheduling_type === "continuous";

  // Sync activeTab for Business and Researcher users based on architecture version
  React.useEffect(() => {
    if ((isBusiness || isResearcher) && program?.architecture_version) {
      if (program.architecture_version === 1) {
        setActiveTab('master');
      } else if (program.architecture_version === 2) {
        setActiveTab('history');
      }
    }
  }, [isBusiness, isResearcher, program?.architecture_version]);

  const { data: programChecklistsData, isLoading: isLoadingProgram, refetch: refetchProgram } = useGetProgramChecklistsQuery(programId);
  const { data: runChecklistsData, isLoading: isLoadingRun, refetch: refetchRun } = useGetRunChecklistsQuery(activeRunId as number, { skip: !activeRunId });
  const { data: resProgramsData } = useGetResearcherProgramsQuery(undefined, { skip: userRole !== 1 });

  const checklistsData = runId ? runChecklistsData : programChecklistsData;
  const isLoading = runId ? isLoadingRun : isLoadingProgram;
  const refetch = runId ? refetchRun : refetchProgram;

  // Only fetch templates if user is admin (needed for linking checklists)
  const { data: templatesData } = useGetChecklistTemplatesQuery(
    { active_only: true },
    { skip: !isAdmin } // Skip this query for non-admin users
  );
  const [linkChecklist] = useLinkChecklistToProgramMutation();
  const [unlinkChecklist] = useUnlinkChecklistFromProgramMutation();
  const [freezeChecklist] = useFreezeProgramChecklistMutation();
  const [unfreezeChecklist] = useUnfreezeProgramChecklistMutation();
  const [unlinkRunChecklist] = useUnlinkChecklistFromRunMutation();
  const [finalizeAdminReview] = useFinalizeAdminReviewMutation();

  const handleLinkChecklist = async (id?: number) => {
    const targetRunId = modalTargetRunId || runId;
    if (!id && !targetRunId) {
      setConfirmModal({
        isOpen: true,
        title: "Create Custom Master Checklist",
        message: "Please enter a name for your custom master checklist. Once created, you will be taken to the checklist editor page to add items.",
        type: "success",
        confirmLabel: "Create & Open",
        showInput: true,
        inputPlaceholder: "Checklist name (e.g., API Security Checklist)...",
        inputValueRequired: true,
        onConfirm: async (customName) => {
          if (!customName || !customName.trim()) {
            toast.error("Checklist name is required");
            return;
          }
          try {
            const result = await linkChecklist({ 
              programId: Number(programId), 
              custom_name: customName.trim()
            }).unwrap();
            
            toast.success("Custom master checklist created successfully!");
            setShowLinkModal(false);
            
            if (result?.data?.program_checklist_id) {
              navigate(`/dashboard/programs/${programId}/checklists/${result.data.program_checklist_id}`);
            } else {
              refetch();
            }
          } catch (err: any) {
            toast.error(err?.data?.error || err?.data?.message || err?.message || "Failed to create checklist");
          }
        }
      });
      return;
    }

    try {
      if (targetRunId) {
        await linkTemplateToRun({
          runId: Number(targetRunId),
          programChecklistId: id
        }).unwrap();
        toast.success("Blueprint linked to run snapshot successfully");
      } else {
        await linkChecklist({ 
          programId: Number(programId), 
          templateId: id ? Number(id) : undefined 
        }).unwrap();
        toast.success(id ? "Checklist linked to program master" : "Custom master checklist created");
      }
      setShowLinkModal(false);
      setModalTargetRunId(undefined);
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.error || err?.data?.message || err?.message || "Failed to link/create checklist");
    }
  };

  const handleUnlinkChecklist = async (checklistId: number, checklistName: string, subRunId?: number, completionPerc: number = 0) => {
    const isRun = !!subRunId || !!runId;
    const finalRunId = subRunId || runId;

    if (isRun && completionPerc > 0) {
      toast.error(`Cannot unlink "${checklistName}": This run snapshot is active and has researcher progress (${Math.round(completionPerc)}%). To maintain audit integrity, it cannot be removed.`);
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: "Unlink Checklist",
      message: `Are you sure you want to unlink "${checklistName}"?`,
      type: "danger",
      confirmLabel: "Unlink",
      onConfirm: async () => {
        try {
          if (isRun) {
            await unlinkRunChecklist({ runId: Number(finalRunId), checklistId }).unwrap();
            toast.success("Run snapshot unlinked successfully");
          } else {
            await unlinkChecklist({ programId, checklistId }).unwrap();
            toast.success("Master checklist unlinked successfully");
          }
          refetch();
        } catch (err: any) {
          const detailMsg = err?.data?.error || err?.data?.message || err?.message || "";
          if (detailMsg.toLowerCase().includes("linked to any runs") || detailMsg.toLowerCase().includes("snapshotted")) {
            // Keep a slightly more user-friendly wrapper for this specific common error
            toast.error(detailMsg || `Cannot unlink Master Checklist: It is currently snapshotted to active testing runs.`, { duration: 6000 });
          } else {
            toast.error(detailMsg || `Failed to unlink checklist from ${isRun ? 'run' : 'program'}`);
          }
        }
      }
    });
  };

  const handleFreezeChecklist = async (checklistId: number, checklistName: string) => {
    setConfirmModal({
      isOpen: true,
      title: "Freeze Checklist",
      message: `Are you sure you want to freeze "${checklistName}"? Once frozen, it will be visible to business users and cannot be edited until unfrozen.`,
      type: "warning",
      confirmLabel: "Freeze",
      onConfirm: async () => {
        try {
          await freezeChecklist(checklistId).unwrap();
          toast.success("Checklist frozen successfully");
          refetch();
        } catch (err: any) {
          toast.error(err?.data?.error || err?.data?.message || err?.message || "Failed to freeze checklist");
        }
      }
    });
  };

  const handleUnfreezeChecklist = async (checklistId: number, checklistName: string, isRunSnapshot: boolean = false, completionPerc: number = 0) => {
    if (isRunSnapshot && completionPerc > 0) {
      toast.error("Cannot unfreeze: researcher action detected in this run snapshot.");
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: "Unfreeze Checklist",
      message: `Are you sure you want to unfreeze "${checklistName}"? It will no longer be visible to business users.`,
      type: "warning",
      confirmLabel: "Unfreeze",
      onConfirm: async () => {
        try {
          await unfreezeChecklist(checklistId).unwrap();
          toast.success("Checklist unfrozen successfully");
          refetch();
        } catch (err: any) {
          toast.error(err?.data?.error || err?.data?.message || err?.message || "Failed to unfreeze checklist");
        }
      }
    });
  };

  // Rule of Hooks: Call all hooks before any conditional returns
  const { runs, refetch: refetchRuns } = useProgramRuns(programId);

  const allChecklists = checklistsData?.data || [];
  const linkedTemplateIds = React.useMemo(() => 
    allChecklists.map((c: ProgramChecklist) => c.template_id),
    [allChecklists]
  );
  
  // Extract primary types from asset
  const assetPrimaryTypes: string[] = React.useMemo(() => {
    if (!asset || !asset.primary_type) return [];
    try {
      if (typeof asset.primary_type === 'string') {
        const parsed = JSON.parse(asset.primary_type);
        return Array.isArray(parsed) ? parsed : [parsed];
      }
      return Array.isArray(asset.primary_type) ? asset.primary_type : [asset.primary_type];
    } catch (e) {
      return [];
    }
  }, [asset]);

  const availableTemplates = React.useMemo(() => {
    const templates = templatesData?.data?.filter(
      t => !linkedTemplateIds.includes(t.template_id)
    ) || [];

    if (assetPrimaryTypes.length === 0) return templates;

    // Add recommendation flag and sort
    return [...templates].map(t => {
      const isRecommended = t.targetMappings?.some((m: any) => 
        assetPrimaryTypes.includes(m.target_type)
      );
      return { ...t, isRecommended };
    }).sort((a, b) => {
      if (a.isRecommended && !b.isRecommended) return -1;
      if (!a.isRecommended && b.isRecommended) return 1;
      return 0;
    });
  }, [templatesData, linkedTemplateIds, assetPrimaryTypes]);

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-8 shadow">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
            <p className="text-gray-600 font-medium">Loading checklists...</p>
          </div>
        </div>
      </div>
    );
  }



  // Researchers should NOT see Master checklists for V2 programs (Continuous/One-off)
  // They strictly work on Run-level snapshots.
  // HOWEVER: We now show their specific assignment cards here if no runId is selected.
  
  if (isResearcher && isV2 && !runId) {
    const researcherAssignments = resProgramsData?.data?.filter(p => p.program_id === Number(programId)) || [];
    
    if (researcherAssignments.length === 0) {
      return (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 py-16 text-center bg-white/50">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow-md mb-6">
            <FiCheckCircle className="w-10 h-10 text-slate-300" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">No Assignments Found</h3>
          <p className="mt-2 text-slate-500 max-w-xs mx-auto mb-8 font-medium">
            You don't have any active checklist assignments for this program yet.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-1.5 h-6 rounded-full bg-blue-600"></div>
          <h3 className="text-lg font-bold text-slate-900">My Testing Assignments</h3>
          <span className="ml-2 px-2 py-0.5 text-xs font-bold text-slate-400 bg-slate-100 rounded-full">
            {researcherAssignments.length}
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {researcherAssignments.map(assignment => (
            <ResearcherAssignmentCard
              key={`${assignment.program_id}-${assignment.run_id || 'master'}`}
              program={assignment}
              onClick={() => {
                const url = `/dashboard/researcher-checklists/programs/${assignment.program_id}?runId=${assignment.run_id || ''}`;
                navigate(url);
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  const checklists = isProgramGuest
    ? allChecklists.filter((c: ProgramChecklist) => c.is_frozen)
    : allChecklists;

  // If in a run modal, show checklists from master program that aren't already in the target run
  const linkingRunId = modalTargetRunId || (runId ? Number(runId) : undefined);
  const masterChecklistsNotInRun = linkingRunId 
    ? programChecklistsData?.data?.filter((mc: any) => 
        !(runChecklistsData?.data || []).some((rc: any) => rc.program_checklist_id === mc.program_checklist_id)
      ) 
    : [];

  return (
    <div className="min-h-screen bg-slate-50/30">
      <div className="max-w-7xl mx-auto space-y-6 pb-8">
        {/* Tab Navigation - Standalone Rounded Card */}
        <div className="flex items-center gap-10 bg-white rounded-2xl border border-gray-200 px-8 shadow-sm">
        {(!isResearcher ? (!isBusiness || program?.architecture_version === 1) : program?.architecture_version === 1) && (
          <button
            onClick={() => setActiveTab('master')}
            className={`py-5 px-1 text-sm font-bold transition-all border-b-2 ${
              activeTab === 'master'
                ? 'border-[#58CC02] text-slate-900'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            Master Program Checklists
          </button>
        )}
        {(!isResearcher ? (!isBusiness || program?.architecture_version === 2) : program?.architecture_version === 2) && (
          <button
            onClick={() => setActiveTab('history')}
            className={`py-5 px-1 text-sm font-bold transition-all border-b-2 ${
              activeTab === 'history'
                ? 'border-[#58CC02] text-slate-900'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <div className="flex items-center gap-2.5">
              Security Run History
              <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full transition-colors ${
                activeTab === 'history'
                  ? 'bg-[#58CC021A] text-[#58CC02]'
                  : 'bg-slate-100 text-slate-400'
              }`}>
                {runs.length}
              </span>
            </div>
          </button>
        )}
      </div>

      <div className="space-y-6 pb-8">
        {/* Header content based on active tab - Isolated Cards */}
        {activeTab === 'master' ? (
          <div className="rounded-2xl bg-white border border-gray-100 p-6 shadow-sm relative overflow-hidden flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-5 relative z-10">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-lg border bg-[#010D2C] border-slate-700">
                <FiLayers className="text-[#58CC02] w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight whitespace-nowrap">Master Program Checklists</h2>
                  <span className="bg-[#58CC021A] text-[#58CC02] text-[8px] font-black px-2 py-0.5 rounded-full border border-[#58CC0233] uppercase tracking-tighter whitespace-nowrap">
                    Master Template
                  </span>
                </div>
                <p className="text-sm text-slate-500 font-medium leading-relaxed">
                  These are the long-term master checklists for the product. They serve as the baseline templates for all subsequent testing runs.
                </p>
              </div>
            </div>
            
            {isAdmin && (
              <div className="flex items-center gap-3 relative z-10">
                {!isContinuousV2 && (
                  <button
                    onClick={() => {
                      setWizardTargetRunId(undefined);
                      setShowWizard(true);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-[#58CC02] px-5 py-2.5 font-medium text-white text-[13px] whitespace-nowrap shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all outline-none"
                  >
                    <FiZap className="w-4 h-4" />
                    Generate Checklist
                  </button>
                )}
                <button
                  onClick={() => {
                    setModalTargetRunId(undefined);
                    setShowLinkModal(true);
                  }}
                  className="flex items-center gap-2 rounded-lg bg-[#010D2C] px-5 py-2.5 font-medium text-white text-[13px] whitespace-nowrap shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all outline-none"
                >
                  <FiPlus className="w-4 h-4" />
                  Link New Checklist
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-gray-100 p-6 shadow-sm relative overflow-hidden flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-5 relative z-10 min-w-0">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-lg border bg-[#010D2C] border-slate-700">
                 <FiLayers className="text-white w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight whitespace-nowrap">Security Run History</h2>
                </div>
                <p className="text-sm text-slate-500 font-medium leading-relaxed">
                  Execute and track security checklists across your development and deployment cycles.
                </p>
              </div>
            </div>
          </div>
        )}


        {/* Section Header - Outside flex to allow side-panel alignment with table top */}
        {activeTab === 'master' && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-6 rounded-full bg-[#58CC02]"></div>
              <h3 className="text-lg font-bold text-slate-900">Available Master Checklists</h3>
              <span className="ml-2 px-2 py-0.5 text-xs font-bold text-slate-400 bg-slate-100 rounded-full">
                {checklists.length}
              </span>
            </div>
          </div>
        )}

        {/* Content Area Rendering */}
        <div className="relative flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            {activeTab === 'master' ? (
              <section className="animate-in fade-in slide-in-from-bottom-2 duration-500">

                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-[#F8FAFC] border-b border-gray-100">
                      <tr>
                        <th className="px-6 py-4 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Name</th>
                        <th className="px-6 py-4 text-[11px] font-semibold text-slate-400 uppercase tracking-widest text-center">Type</th>
                        <th className="px-6 py-4 text-[11px] font-semibold text-slate-400 uppercase tracking-widest text-center">Status</th>
                        <th className="px-6 py-4 text-[11px] font-semibold text-slate-400 uppercase tracking-widest text-center">Items</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(!programChecklistsData?.data || programChecklistsData.data.length === 0) ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-20 text-center">
                            <FiSearch className="mx-auto w-10 h-10 text-slate-200 mb-4" />
                            <p className="text-slate-400 font-bold">No master checklists found</p>
                          </td>
                        </tr>
                      ) : (
                        (isProgramGuest 
                          ? programChecklistsData.data.filter((c: ProgramChecklist) => c.is_frozen) 
                          : programChecklistsData.data
                        ).map((checklist: ProgramChecklist) => (
                          <tr
                            key={checklist.program_checklist_id}
                            onClick={() => setSelectedChecklist(checklist)}
                            className={`group cursor-pointer transition-all hover:bg-blue-50/50 ${
                              selectedChecklist?.program_checklist_id === checklist.program_checklist_id
                                ? 'bg-blue-50/80 shadow-inner'
                                : ''
                            }`}
                          >
                            <td className="px-6 py-5">
                              <p className="font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                                {checklist.checklist_name}
                              </p>
                            </td>
                            <td className="px-6 py-5">
                              <div className="flex justify-center">
                                <span className="bg-green-100/50 text-green-700 text-[10px] font-medium px-2.5 py-1 rounded-lg border border-green-200/50 uppercase tracking-tight">
                                  Master Program
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-5">
                              <div className="flex items-center justify-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${checklist.is_frozen ? 'bg-blue-500' : 'bg-amber-500'}`}></div>
                                <span className={`text-[11px] font-medium uppercase tracking-widest ${checklist.is_frozen ? 'text-blue-600' : 'text-amber-600'}`}>
                                  {checklist.is_frozen ? 'FROZEN' : 'DRAFT'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-5 text-center">
                              <span className="text-sm font-medium text-slate-700 tabular-nums">
                                {checklist.items?.length || 0}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : (
              <section className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                
                {runs.length === 0 ? (
                  <div className="rounded-3xl border-2 border-dashed border-slate-200 py-20 text-center bg-white/50 shadow-inner">
                    <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-3xl bg-white shadow-xl mb-8 border border-slate-100">
                      <FiPlay className="w-10 h-10 text-slate-200" />
                    </div>
                    <h3 className="text-2xl font-black text-slate-900 mb-2">No Runs Started</h3>
                    <p className="text-slate-500 max-w-sm mx-auto font-medium leading-relaxed">
                      Specific testing cycles haven't been initiated yet. You'll see run-specific security checkpoints here once they begin.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {runs.map(run => (
                      <RunChecklistsSection 
                        key={run.run_id} 
                        run={run} 
                        programId={programId} 
                        userRole={userRole} 
                        isAdmin={isAdmin} 
                        canEdit={canEdit} 
                        isContinuousV2={isContinuousV2}
                        onOpenLinkModal={(rId) => {
                          setModalTargetRunId(rId);
                          setShowLinkModal(true);
                        }}
                        onOpenWizard={(rId) => {
                          setWizardTargetRunId(rId);
                          setShowWizard(true);
                        }}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>

          {/* Side Panel for Selected Master Checklist */}
          {activeTab === 'master' && selectedChecklist && (
            <div className="lg:w-96 shrink-0 animate-in slide-in-from-right duration-300">
              <div className="sticky top-4 bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div>
                  {/* Header & Title Section */}
                  <div className="p-6 pb-3">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="bg-green-50 text-green-700 text-[8px] font-black px-2 py-0.5 rounded border border-green-200 uppercase tracking-tighter">
                          Master Program
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[8px] font-black tracking-tighter border flex items-center gap-1 ${
                           selectedChecklist.is_frozen 
                             ? 'bg-blue-50 text-blue-600 border-blue-100' 
                             : 'bg-amber-50 text-amber-600 border-amber-100'
                         }`}>
                           {selectedChecklist.is_frozen ? <FiLock className="w-2.5 h-2.5" /> : <FiUnlock className="w-2.5 h-2.5" />}
                           {selectedChecklist.is_frozen ? 'FROZEN' : 'DRAFT'}
                         </span>
                         {selectedChecklist.is_admin_reviewed && (
                           <span className="px-2 py-0.5 rounded-full text-[8px] font-black tracking-tighter border bg-green-50 text-green-600 border-green-100 flex items-center gap-1">
                             <FiCheckCircle className="w-2.5 h-2.5" />
                             APPROVED
                           </span>
                         )}
                       </div>
                       <button 
                         onClick={() => setSelectedChecklist(null)}
                         className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                       >
                        <FiX className="text-gray-400" />
                      </button>
                    </div>
                    <h4 className="text-lg font-semibold text-slate-900 leading-tight">
                      {selectedChecklist.checklist_name}
                    </h4>
                  </div>

                  <div className="border-t border-gray-100 w-full" />

                  {/* Body Content */}
                  <div className="p-6 pt-6 space-y-6">
                    <div className="space-y-2">
                      <span className="text-[10px] font-medium text-slate-400 uppercase tracking-widest">Description</span>
                      {selectedChecklist.description ? (
                        <p className="text-[13px] text-slate-600 font-medium leading-relaxed">
                          {selectedChecklist.description}
                        </p>
                      ) : (
                        <p className="text-sm text-slate-400 italic">No description provided</p>
                      )}
                    </div>

                    <div className="bg-[#f8fafc] rounded-lg p-3.5 border border-slate-100/50">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-widest">Total Items</span>
                        <span className="text-lg font-bold text-slate-900 tabular-nums">
                          {selectedChecklist.items?.length || 0}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3 pt-2">
                      <button
                        onClick={() => {
                          const path = isResearcher 
                            ? `/dashboard/researcher-checklists/programs/${programId}`
                            : `/dashboard/programs/${programId}/checklists/${selectedChecklist.program_checklist_id}`;
                          navigate(path);
                        }}
                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#010D2C] text-white rounded-lg font-medium text-sm hover:translate-y-[-1px] transition-all"
                      >
                        <FiExternalLink className="w-4 h-4" />
                        Open Checklist
                      </button>

                      {/* Download Checklist PDF — gated on admin approval */}
                      <DownloadChecklistPDFButton
                        checklist={selectedChecklist}
                        programName={program?.title || `Program ${programId}`}
                        isAdmin={isAdmin}
                      />

                      {isAdmin && !isV2 && !selectedChecklist.is_admin_reviewed && !selectedChecklist.checklist_name?.toLowerCase().includes("download") && (
                          <button
                            onClick={() => {
                              setConfirmModal({
                                isOpen: true,
                                title: "Approve & Finalize Checklist",
                                message: "Are you sure you want to approve and finalize this checklist? Once finalized, business clients will be able to download the checklist PDF.",
                                type: "success",
                                confirmLabel: "Approve & Finalize",
                                onConfirm: async () => {
                                  try {
                                    await finalizeAdminReview({ 
                                      programId, 
                                      checklistId: selectedChecklist.program_checklist_id 
                                    }).unwrap();
                                    toast.success("Checklist approved and finalized!");
                                    setSelectedChecklist(prev => prev ? { ...prev, is_admin_reviewed: true } : null);
                                    refetch();
                                  } catch (err: any) {
                                    toast.error(err?.data?.message || "Failed to finalize review");
                                  }
                                }
                              });
                            }}
                            className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded-lg font-medium text-xs hover:bg-green-700 transition-all"
                          >
                            <FiCheckCircle className="w-4 h-4" />
                            Approve & Finalize Checklist
                          </button>
                        )}

                      {canEdit && (
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => {
                              if (selectedChecklist.is_frozen) {
                                handleUnfreezeChecklist(selectedChecklist.program_checklist_id, selectedChecklist.checklist_name, false, 0);
                              } else {
                                handleFreezeChecklist(selectedChecklist.program_checklist_id, selectedChecklist.checklist_name);
                              }
                            }}
                            className={`flex items-center justify-center gap-2 py-2 px-2 rounded-lg text-[11px] font-medium transition-all border-2 ${
                              selectedChecklist.is_frozen 
                                ? 'bg-white border-blue-100 text-blue-600 hover:bg-blue-50' 
                                : 'bg-white border-[#58CC02] text-[#58CC02] hover:bg-green-50'
                            }`}
                          >
                            {selectedChecklist.is_frozen ? <FiLock size={14} /> : <FiUnlock size={14} />}
                            {selectedChecklist.is_frozen ? 'Unfreeze' : 'Freeze'}
                          </button>
                          <button
                            onClick={() => handleUnlinkChecklist(selectedChecklist.program_checklist_id, selectedChecklist.checklist_name, undefined, 0)}
                            className="flex items-center justify-center gap-2 py-2 px-2 rounded-lg text-[11px] font-medium bg-white border-2 border-[#58CC02] text-[#58CC02] hover:bg-green-50 transition-all"
                          >
                            <FiZap className="w-3.5 h-3.5 rotate-180" />
                            Unlink
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generate Wizard Modal */}
      {showWizard && (
        <ChecklistGenerator
          programId={programId}
          runId={wizardTargetRunId}
          runName={runs.find(r => r.run_id === wizardTargetRunId)?.run_title}
          asset={asset}
          startDate={runs.find(r => r.run_id === wizardTargetRunId)?.start_date}
          endDate={runs.find(r => r.run_id === wizardTargetRunId)?.end_date}
          onClose={() => {
            setShowWizard(false);
            setWizardTargetRunId(undefined);
            refetch();
            if (typeof refetchRuns === 'function') refetchRuns();
            if (typeof refetchProgramData === 'function') refetchProgramData();
          }}
        />
      )}

      {/* Link Checklist Modal (Updated visual style) */}
      {showLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
          <div className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            {/* Modal Header */}
            <div className="border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-white">
                  <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <div>
                    <h3 className="text-xl font-bold">
                      {runId ? 'Link Snapshot Checklist' : 'Link Master Checklist'}
                    </h3>
                    <p className="text-sm text-blue-100">
                      {runId ? `Add a test execution snapshot to Run #${runId}` : 'Add a global master checklist to this program'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="rounded-lg p-1 text-white transition-colors hover:bg-white hover:bg-opacity-20"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="max-h-[calc(85vh-120px)] overflow-y-auto p-6">
              {/* Context-Specific Linking Options */}
              {(modalTargetRunId || runId) ? (
                <>
                  {programChecklistsData?.data?.length === 0 ? (
                    /* CASE 1: Master is empty -> Direct user to Master Program */
                    <div className="rounded-2xl border-2 border-dashed border-red-200 py-16 text-center bg-red-50/30">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm mb-4">
                        <FiLayers className="w-8 h-8 text-red-300" />
                      </div>
                      <h4 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Master Blueprint Required</h4>
                      <p className="mt-2 text-slate-500 max-w-sm mx-auto font-medium text-sm leading-relaxed mb-8">
                        No Master Blueprints are defined for this program yet. 
                        You must link or create a checklist in the **Master Program** before it can be used for test runs.
                      </p>
                      <button
                        onClick={() => {
                          setShowLinkModal(false);
                          setViewRunId(undefined); // Switch back to Master view
                          toast.success("Switching to Master Program view");
                        }}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#010D2C] text-[#58CC02] rounded-xl font-black text-sm hover:translate-y-[-2px] hover:shadow-lg transition-all"
                      >
                        <FiLayers />
                        Go to Master Program
                      </button>
                    </div>
                  ) : masterChecklistsNotInRun && masterChecklistsNotInRun.length > 0 ? (
                    /* CASE 2: Master has items -> Show them for snapshotting */
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-1.5 h-4 rounded-full bg-[#58CC02]"></div>
                        <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Available Master Blueprints</h4>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        {masterChecklistsNotInRun.map(mc => (
                          <button
                            key={mc.program_checklist_id}
                            onClick={() => handleLinkChecklist(mc.program_checklist_id)}
                            className="flex items-center justify-between p-4 rounded-xl border-2 border-slate-100 bg-slate-50 hover:border-[#58CC02] hover:bg-white transition-all group"
                          >
                            <div className="text-left">
                              <p className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{mc.checklist_name}</p>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Link as Snapshot to Run</p>
                            </div>
                            <FiPlus className="text-slate-300 group-hover:text-[#58CC02] w-5 h-5 transition-colors" />
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* CASE 3: All Items already snapshotted */
                    <div className="rounded-2xl border-2 border-dashed border-slate-200 py-16 text-center bg-slate-50/50">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm mb-4">
                        <FiCheckCircle className="w-8 h-8 text-slate-300" />
                      </div>
                      <h4 className="text-lg font-bold text-slate-900">All Master Blueprints Linked</h4>
                      <p className="mt-2 text-slate-500 max-w-xs mx-auto font-medium text-sm">
                        All checklists from the Program Master are already snapshotted to this run. Add new checklists to the master program list first if needed.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                /* PROGRAM CONTEXT: Show custom option and global templates */
                <>
                  <div className="mb-8 rounded-xl border-2 border-dashed border-blue-200 bg-blue-50 p-6 text-center transition-all hover:border-blue-400 hover:bg-blue-100">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white shadow-md">
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <h4 className="mt-3 text-lg font-bold text-blue-900">Custom Master Checklist</h4>
                    <p className="mt-1 text-sm text-blue-700">Start with a blank master checklist and add items from scratch</p>
                    <button
                      onClick={() => navigate('/dashboard/checklist-templates/create')}
                      className="mt-4 rounded-lg bg-blue-600 px-6 py-2 text-sm font-bold text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md"
                    >
                      Create Custom Master
                    </button>
                  </div>

                  <div className="relative mb-6 flex items-center">
                    <div className="flex-grow border-t border-gray-200"></div>
                    <span className="shrink px-4 text-xs font-bold uppercase tracking-wider text-gray-400">
                      Or use a global template
                    </span>
                    <div className="flex-grow border-t border-gray-200"></div>
                  </div>

                  {availableTemplates.length === 0 ? (
                    <div className="rounded-lg bg-gray-50 py-12 text-center">
                      <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="mt-4 text-gray-600 font-medium">
                        All available templates are already linked to this program.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {availableTemplates.map(template => (
                        <div
                          key={template.template_id}
                          className="group flex items-center justify-between rounded-xl border-2 border-gray-200 p-5 transition-all hover:border-blue-300 hover:shadow-md"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="text-lg font-bold text-gray-800">
                                {template.template_name}
                              </h4>
                              {(template as any).isRecommended && (
                                <span className="bg-amber-100 text-amber-700 text-[10px] font-black px-2 py-0.5 rounded-full border border-amber-200 uppercase tracking-tight flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                  Recommended
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-gray-600">
                              {template.description}
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {template.targetMappings?.map(mapping => (
                                <span
                                  key={mapping.mapping_id}
                                  className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800"
                                >
                                  {mapping.target_type}
                                </span>
                              ))}
                            </div>
                          </div>
                          <button
                            onClick={() => handleLinkChecklist(template.template_id)}
                            className="ml-6 rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md"
                          >
                            <span className="flex items-center gap-2">
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              Link
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        type={confirmModal.type}
        showInput={confirmModal.showInput}
        inputPlaceholder={confirmModal.inputPlaceholder}
        inputValueRequired={confirmModal.inputValueRequired}
      />
      </div>
    </div>
  );
};

export default ProgramChecklistsView;
